import * as Sentry from "@sentry/react";

// App version from package.json or env var
const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";

// Log categories
export const LogCategory = {
  AUTH: "auth",
  SESSION: "session",
  ACTIVITY: "activity",
  ERROR: "error",
  LIVEKIT: "livekit",
  SESSION_HISTORY: "session_history",
  EDUCATOR_DASHBOARD: "educator_dashboard",
  API: "api",
  TECH_CHECK: "tech_check",
};

// Log levels
export const LogLevel = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
};

/**
 * Sets the current user context for Sentry (PII-safe: user IDs only).
 *
 * Two calling conventions are supported:
 *   setUser("firebase-uid")                       // legacy — Firebase UID only
 *   setUser({ okoUserId, firebaseUid })           // preferred — OKO SQL id + Firebase UID
 *
 * When both ids are supplied, `id` (the Sentry primary key) prefers the OKO SQL
 * id so events are joinable against the OKO users table; the Firebase UID is
 * still attached as a secondary tag.
 */
export const setUser = (identity) => {
  if (identity == null || identity === "") {
    Sentry.setUser(null);
    return;
  }

  if (typeof identity === "string" || typeof identity === "number") {
    Sentry.setUser({ id: identity });
    return;
  }

  const { okoUserId, firebaseUid } = identity;
  const primaryId = okoUserId ?? firebaseUid;
  if (!primaryId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(primaryId),
    firebase_uid: firebaseUid ?? null,
    oko_user_id: okoUserId ?? null,
  });
};

/**
 * Shared session envelope that every structured log event is auto-enriched with.
 * Lives at module scope so callers don't need to thread these fields through —
 * set it once (typically right after the session prefetch resolves) and every
 * subsequent log*() call picks the fields up automatically.
 */
let currentSessionContext = {};

export const getSessionContext = () => currentSessionContext;
export const clearSessionContext = () => {
  currentSessionContext = {};
  Sentry.setContext("session", null);
};

/**
 * Sets session context for Sentry.
 *
 * Fields beyond the core three are optional — omit them and the envelope will
 * only carry what you passed. Passing a field as `undefined` preserves the
 * current value; pass `null` to explicitly clear a field.
 *
 * @param {Object} context
 * @param {string} context.sessionId       - Current session ID
 * @param {string} context.groupCode       - Current group code (class code)
 * @param {string} context.activityType    - Current activity type
 * @param {string} context.sessionStatus   - Session status (not_started, in_progress, complete)
 * @param {string} context.livekitUrl      - LiveKit room URL
 */
export const setSessionContext = (context = {}) => {
  const next = { ...currentSessionContext };
  Object.keys(context).forEach((key) => {
    if (context[key] !== undefined) {
      next[key] = context[key];
    }
  });
  currentSessionContext = next;
  Sentry.setContext("session", next);
};

/**
 * Client (device/browser/OS) envelope, set once at app boot and merged into
 * every structured log. Sentry derives its own browser/OS from the raw user
 * agent, which reports an iPad in "desktop site" mode as a Mac, so the
 * feature-checked values from useClientInfo are attached explicitly and the
 * device type is also a tag for filtering. Pass null to clear (tests).
 */
let currentClientContext = {};

export const setClientContext = (clientInfo) => {
  if (!clientInfo) {
    currentClientContext = {};
    Sentry.setContext("client", null);
    return;
  }
  const { device = {}, browser = {}, os = {} } = clientInfo;
  currentClientContext = {
    device_type: device.type,
    device_model: [device.vendor, device.model].filter(Boolean).join(" ") || undefined,
    browser: [browser.name, browser.version].filter(Boolean).join(" ") || undefined,
    os: [os.name, os.version].filter(Boolean).join(" ") || undefined,
  };
  Sentry.setContext("client", currentClientContext);
  Sentry.setTag("device.type", device.type);
};

/**
 * Selected mic/speaker envelope (labels only — device ids are per-origin
 * hashes and mean nothing in Sentry). Set by useAudioDeviceTelemetry whenever
 * the student's audio setup changes, merged into every structured log, and
 * attached to issues as context so a device refusal shows what was refused.
 * Pass null to clear (tests).
 */
let currentAudioDeviceContext = {};

export const setAudioDeviceContext = (snapshot) => {
  if (!snapshot) {
    currentAudioDeviceContext = {};
    Sentry.setContext("audio_devices", null);
    return;
  }
  currentAudioDeviceContext = { ...snapshot };
  Sentry.setContext("audio_devices", currentAudioDeviceContext);
  Sentry.setTag("audio.output_kind", snapshot.speaker_kind);
};

/**
 * Core logging function that wraps Sentry.captureMessage and Sentry.logger
 * @param {string} message - Log message
 * @param {string} level - Log level (info, warning, error)
 * @param {string} category - Log category
 * @param {Object} data - Additional data to include
 */
const log = (message, level, category, data = {}) => {
  // Strip undefined fields from the caller's data so the shared session
  // envelope can show through when specific helpers (logTechCheck, logSession,
  // etc.) destructure envelope keys as options and pass them along blank.
  const definedData = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) definedData[key] = value;
  }
  const logData = {
    log_source: category,
    app_version: APP_VERSION,
    timestamp: new Date().toISOString(),
    // Client + session envelopes — auto-injected from whatever
    // `setClientContext` / `setSessionContext` have set so far.
    // Caller-supplied defined fields win on key collision.
    ...currentClientContext,
    ...currentAudioDeviceContext,
    ...currentSessionContext,
    ...definedData,
  };

  // Use Sentry.logger for structured logging (requires SDK 9.41.0+)
  if (Sentry.logger) {
    switch (level) {
      case LogLevel.WARNING:
        Sentry.logger.warn(message, logData);
        break;
      case LogLevel.ERROR:
        Sentry.logger.error(message, logData);
        break;
      default:
        Sentry.logger.info(message, logData);
    }
  }

  // Use captureMessage for error level to ensure it appears in Issues
  if (level === LogLevel.ERROR) {
    Sentry.captureMessage(message, {
      level: "error",
      tags: { category },
      extra: logData,
    });
  }
};

/**
 * Log authentication events
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.userId - User ID (PII-safe)
 * @param {string} options.authMethod - Authentication method (google, clever, etc.)
 * @param {Object} options.data - Additional data
 */
export const logAuth = (message, { level = LogLevel.INFO, userId, authMethod, ...data } = {}) => {
  log(message, level, LogCategory.AUTH, {
    userId,
    authMethod,
    ...data,
  });
};

/**
 * Log session events
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.sessionId - Session ID
 * @param {string} options.groupCode - Group code
 * @param {string} options.role - User role in session
 * @param {Object} options.data - Additional data
 */
export const logSession = (message, { level = LogLevel.INFO, sessionId, groupCode, role, ...data } = {}) => {
  log(message, level, LogCategory.SESSION, {
    sessionId,
    groupCode,
    role,
    ...data,
  });
};

/**
 * Log activity events
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.activityType - Activity type (consensus, calculator, etc.)
 * @param {string} options.activityId - Activity ID
 * @param {string} options.sessionId - Session ID
 * @param {Object} options.data - Additional data
 */
export const logActivity = (message, { level = LogLevel.INFO, activityType, activityId, sessionId, ...data } = {}) => {
  log(message, level, LogCategory.ACTIVITY, {
    activityType,
    activityId,
    sessionId,
    ...data,
  });
};

/**
 * Log LiveKit events (connection, quality, tracks, devices)
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.event - LiveKit event type (connection_initiated, connected, disconnected, reconnecting, etc.)
 * @param {string} options.sessionId - Session ID
 * @param {string} options.roomUrl - LiveKit room URL
 * @param {Object} options.data - Additional data
 */
export const logLiveKit = (message, { level = LogLevel.INFO, event, sessionId, roomUrl, ...data } = {}) => {
  log(message, level, LogCategory.LIVEKIT, {
    event,
    sessionId,
    roomUrl,
    ...data,
  });
};

/**
 * Log session history events (educator analytics/progress reports)
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.event - Event type (filter_applied, session_opened, tab_switched, load_error)
 * @param {string} options.sessionId - Session ID being viewed
 * @param {string} options.classroomId - Classroom filter ID
 * @param {string} options.tab - Tab name (summary, questions, transcript)
 * @param {Object} options.data - Additional data
 */
export const logSessionHistory = (message, { level = LogLevel.INFO, event, sessionId, classroomId, tab, ...data } = {}) => {
  log(message, level, LogCategory.SESSION_HISTORY, {
    event,
    sessionId,
    classroomId,
    tab,
    ...data,
  });
};

/**
 * Log educator dashboard events (group code management, settings)
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.event - Event type (classroom_filter_changed, classroom_renamed, group_code_edit_clicked, etc.)
 * @param {string} options.groupCode - Group code being acted upon
 * @param {string} options.classroomId - Classroom ID
 * @param {Object} options.data - Additional data
 */
export const logEducatorDashboard = (message, { level = LogLevel.INFO, event, groupCode, classroomId, ...data } = {}) => {
  log(message, level, LogCategory.EDUCATOR_DASHBOARD, {
    event,
    groupCode,
    classroomId,
    ...data,
  });
};

/**
 * Log API request events (critical endpoints only)
 * @param {string} message - Log message
 * @param {Object} options - Logging options
 * @param {string} options.level - Log level (default: info)
 * @param {string} options.endpoint - API endpoint path
 * @param {string} options.endpointName - Normalized endpoint name for filtering
 * @param {string} options.method - HTTP method (GET, POST, etc.)
 * @param {number} options.statusCode - HTTP response status code
 * @param {number} options.duration - Request duration in milliseconds
 * @param {number} options.retryCount - Number of retry attempts (0 if no retries)
 * @param {Object} options.data - Additional data
 */
export const logApi = (message, {
  level = LogLevel.INFO,
  endpoint,
  endpointName,
  method,
  statusCode,
  duration,
  retryCount = 0,
  ...data
} = {}) => {
  log(message, level, LogCategory.API, {
    endpoint,
    endpointName,
    method,
    statusCode,
    duration,
    retryCount,
    ...data,
  });
};

/**
 * Log TechCheck funnel events (pre-session tech check overlay).
 *
 * The common envelope fields (sessionId, groupCode, isSpectator, firebaseUserId,
 * okoUserId, flowElapsedMs) are typically injected by `useTechCheckLogger` —
 * call this function directly only from code that doesn't sit inside the React
 * tree.
 *
 * @param {string} message
 * @param {Object} options
 * @param {string} options.level          - default INFO
 * @param {string} options.event          - snake_case event name (e.g. "tech_check_opened")
 * @param {string} options.step           - current step slug or null
 * @param {string} options.sessionId
 * @param {string} options.groupCode
 * @param {boolean} options.isSpectator
 * @param {string} options.firebaseUserId
 * @param {string|number} options.okoUserId - OKO SQL id (nullable until backend exposes it)
 * @param {number} options.flowElapsedMs  - ms since overlay mount
 */
export const logTechCheck = (message, {
  level = LogLevel.INFO,
  event,
  step,
  sessionId,
  groupCode,
  isSpectator,
  firebaseUserId,
  okoUserId,
  flowElapsedMs,
  ...data
} = {}) => {
  log(message, level, LogCategory.TECH_CHECK, {
    event,
    step,
    sessionId,
    groupCode,
    isSpectator,
    firebaseUserId,
    okoUserId,
    flowElapsedMs,
    ...data,
  });
};

/**
 * Log errors with full context
 * @param {string} message - Error message
 * @param {Error} error - Error object (optional)
 * @param {Object} options - Additional context
 * @param {string} options.category - Error category (default: ERROR). Use LogCategory enum values.
 */
export const logError = (message, error = null, { category = LogCategory.ERROR, ...options } = {}) => {
  let errorData = {};
  if (error instanceof Error) {
    errorData = {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
    };
  } else if (error && typeof error === "object") {
    // Plain rejection object (e.g. API error from useOkoApi:
    // { status, detail, retryCount, ... }). Extract the useful fields so
    // the structured log still carries actionable context.
    errorData = {
      errorName: error.name,
      errorMessage: error.message || error.detail,
      errorStack: error.stack,
      errorStatus: error.status || error.statusCode,
      errorDetail: error.detail,
      retryCount: error.retryCount,
    };
  }

  log(message, LogLevel.ERROR, category, {
    ...errorData,
    ...options,
  });

  // Only call Sentry.captureException for real Error instances. Passing a
  // plain object causes Sentry to emit a noisy synthetic
  // "Object captured as exception with keys: ..." issue with no stack —
  // duplicating the captureMessage issue emitted by log() above.
  if (error instanceof Error) {
    Sentry.captureException(error, {
      tags: { category },
      extra: {
        customMessage: message,
        ...options,
      },
    });
  }
};
